// Privacy policy. Reachable at /privacy with or without sign-in
// (App.tsx routes it before the auth gate). The URL is also what
// you list in App Store Connect → App Privacy → Privacy Policy URL.
//
// Keep the disclosures here in sync with:
//   1. The App Store Connect App Privacy nutrition label
//   2. ios/App/App/PrivacyInfo.xcprivacy
//
// Apple's review will diff these three sources; mismatches get a
// rejection note.

const LAST_UPDATED = "June 24, 2026";

export function PrivacyPolicy() {
  return (
    <div className="min-h-full bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10 md:py-16">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        <article className="prose prose-slate max-w-none space-y-6 text-[15px] text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900">Overview</h2>
            <p>
              ShiftManager is a workforce scheduling tool sold to law
              enforcement and public-safety agencies. The agency
              ("your agency") is the data controller for the
              information you and your supervisors enter into the
              app. ShiftManager and its operator act as a data
              processor on the agency's behalf — we store the data,
              keep it accessible to the people the agency
              authorizes, and never sell, rent, or share it with
              third parties for advertising or analytics.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Information we collect
            </h2>
            <p>
              We collect only what's needed to run the service:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Account information.</strong> Your email
                address, agency assignment, and role level
                (officer, supervisor, owner). Used for sign-in,
                permissions, and sending you notifications.
              </li>
              <li>
                <strong>Workforce content.</strong> Schedule
                assignments, time-off requests, training records,
                rosters, on-call rotations, and any other data
                your agency creates inside the app. Visible only
                to authorized members of the same agency.
              </li>
              <li>
                <strong>Push notification tokens.</strong> When you
                enable push notifications, your device gives us an
                opaque token (Apple Push Notification service or
                browser Web Push) that we store so we can deliver
                alerts. Tokens identify a device, not a person, and
                are revoked the moment a push service tells us
                they're no longer valid.
              </li>
              <li>
                <strong>Operational logs.</strong> Standard request
                logs (timestamps, IPs, error traces) retained
                briefly for debugging and abuse prevention. Not
                used for any other purpose.
              </li>
            </ul>
            <p>
              We do <strong>not</strong> collect device location,
              contacts, photos, microphone, camera, or any tracking
              identifiers (IDFA, fingerprinting). The app does not
              integrate any third-party analytics or advertising
              SDK.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              How we use information
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To authenticate you and authorize access to your agency's data.</li>
              <li>To deliver notifications you've opted into (email, iOS push, browser push).</li>
              <li>To maintain the schedule, roster, and time-off features the agency uses.</li>
              <li>To diagnose and fix bugs reported by the agency.</li>
              <li>To prevent abuse (rate limiting, anomaly detection).</li>
            </ul>
            <p>
              We do not profile you, use your data to train
              machine-learning models, or share it with advertisers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Where data is stored
            </h2>
            <p>
              Data is stored in encrypted databases hosted by
              Supabase (Postgres) in the United States, with
              row-level security enforced per user and per agency.
              Notifications are delivered via Resend (email),
              Apple's Push Notification service (iOS), and
              browser-vendor Web Push services (Chrome, Firefox,
              Safari, Edge). Each push provider receives only the
              encrypted notification payload and your device token
              — nothing about the recipient's identity beyond the
              token itself.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Data retention
            </h2>
            <p>
              Your agency controls the retention of its data. When
              the agency cancels its subscription, agency data is
              archived and then deleted within 90 days. Push tokens
              that become inactive are deleted automatically. You
              can request immediate deletion of your personal data
              by contacting the operator (below).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Your rights
            </h2>
            <p>
              Depending on your jurisdiction (e.g. CCPA, GDPR), you
              may have the right to access, correct, export, or
              delete the personal data we hold about you. To
              exercise these rights, email the operator using the
              contact below. We respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Children
            </h2>
            <p>
              ShiftManager is a workplace tool. We do not knowingly
              collect data from anyone under 18, and the service is
              not directed to children.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Changes to this policy
            </h2>
            <p>
              We may update this policy as the service evolves.
              Material changes will be announced inside the app and
              by email to agency owners. The "Last updated" date at
              the top reflects the most recent revision.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">
              Contact
            </h2>
            <p>
              Questions, requests, or complaints regarding this
              policy or our handling of your data:
            </p>
            <p className="font-mono text-sm">
              ShiftManager Operator<br />
              <a href="mailto:jamesleonbond@gmail.com" className="text-sky-700 hover:underline">
                jamesleonbond@gmail.com
              </a>
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
